import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useState, useEffect, useRef } from 'react';
import { IoSearchOutline } from 'react-icons/io5';
import Modal from '../../General/Modal';
import Loader from '../../General/Loader';
import { getProducts, deleteProduct } from '../../../api/products';
import cogoToast from 'cogo-toast';
import classnames from 'classnames';
import './ProductDeletionModal.scss';
import { getSmartImage } from '../../../helpers/helpers';

/**
 * Modal component for deleting a product with options to orphan or migrate related data
 *
 * @param {Object} props
 * @param {number} props.productId - The ID of the product to delete
 * @param {Function} props.onDeleteSuccess - Callback function called after successful deletion
 */
export const ProductDeletionModal = ({ productId, onDeleteSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteOption, setDeleteOption] = useState('orphan');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const searchInputRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (isMounted.current) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm.length < 3) {
      setSearchResults([]);
      return undefined;
    }

    let cancelled = false;

    const doSearch = async () => {
      setIsSearching(true);
      try {
        const { products } = await getProducts({ query: debouncedSearchTerm, pageSize: 10 });
        if (!cancelled) {
          setSearchResults(products || []);
        }
      } catch (error) {
        console.error('Search error:', error);
        if (!cancelled) {
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    };

    doSearch();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (deleteOption === 'migrate' && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [deleteOption]);

  const handleDeleteProduct = async e => {
    e.preventDefault();
    e.stopPropagation();

    setIsDeleting(true);
    try {
      const params = deleteOption === 'migrate' && selectedProduct ? { migrateToProductId: selectedProduct.id } : {};
      await deleteProduct(productId, params);

      cogoToast.success('Successfully deleted product!', { hideAfter: 1 });
      setIsOpen(false);
      onDeleteSuccess?.();
    } catch (err) {
      console.error('Failed to delete product:', err);
      cogoToast.error(err || 'Failed to delete product');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = e => {
    e.preventDefault();
    e.stopPropagation();

    setIsOpen(false);
    setSearchTerm('');
    setSelectedProduct(null);
    setDeleteOption('orphan');
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  const handleSelectProduct = product => {
    setSelectedProduct(product);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleSelectOrphan = () => {
    setDeleteOption('orphan');
    setSelectedProduct(null);
    setSearchTerm('');
    setSearchResults([]);
  };

  return (
    <>
      <div onClick={() => setIsOpen(true)} className='delete icn'>
        <FontAwesomeIcon icon={faTrash}></FontAwesomeIcon>
      </div>

      {isOpen && (
        <Modal title='Delete Product' close={isDeleting ? () => {} : () => setIsOpen(false)} className='product-deletion-modal'>
          <p>What would you like to do with associated pins, url data, and other product data?</p>

          <div className='delete-data-options'>
            <label>
              <input
                type='radio'
                name='delete-data'
                value='orphan'
                checked={deleteOption === 'orphan'}
                onChange={handleSelectOrphan}
                disabled={isDeleting}
              />
              Orphan related data
            </label>

            <label>
              <input
                type='radio'
                name='delete-data'
                value='migrate'
                checked={deleteOption === 'migrate'}
                onChange={e => setDeleteOption(e.target.value)}
                disabled={isDeleting}
              />
              Migrate to another product
            </label>
          </div>

          {deleteOption === 'migrate' && (
            <div className='product-search-section'>
              {!selectedProduct ? (
                <>
                  <div className='search-input-wrapper'>
                    <IoSearchOutline className='search-icon' size={20} />
                    <input
                      ref={searchInputRef}
                      type='text'
                      placeholder='Search for a product to migrate to'
                      value={searchTerm}
                      onChange={handleSearchChange}
                      disabled={isDeleting}
                    />
                    <Loader variant='circle' className={classnames('loading-icon', { visible: isSearching })} size={24} />
                  </div>

                  {searchResults.length > 0 && (
                    <ul className='search-results'>
                      {searchResults.map(product => (
                        <li key={product.id} className='search-result'>
                          <img src={getSmartImage(product.image)} alt={product.title || product.name} />
                          <div className='product-info'>
                            <p className='product-brand'>{product.brand}</p>
                            <p className='product-title'>
                              {product.title || product.name} <span className='product-id'>#{product.id}</span>
                            </p>
                          </div>
                          <button type='button' onClick={() => handleSelectProduct(product)} disabled={isDeleting}>
                            Select
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <div className='selected-product'>
                  <p>Migrate to:</p>
                  <div className='product-display'>
                    <img src={getSmartImage(selectedProduct.image)} alt={selectedProduct.title || selectedProduct.name} />
                    <div className='product-info'>
                      <p className='product-brand'>{selectedProduct.brand}</p>
                      <p className='product-title'>{selectedProduct.title || selectedProduct.name}</p>
                    </div>
                    <button type='button' onClick={() => setSelectedProduct(null)} disabled={isDeleting}>
                      Change
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className='actions'>
            <button type='button' onClick={handleClose} disabled={isDeleting}>
              Cancel
            </button>
            <button
              className='delete-button'
              type='button'
              onClick={handleDeleteProduct}
              disabled={isDeleting || (deleteOption === 'migrate' && !selectedProduct)}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};
